import axios from 'axios';

// 動態獲取API基礎URL
const getApiBase = (): string => {
  // 優先使用環境變量
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 如果在生產環境（部署後），使用相對路徑（空字符串表示相同域名）
  if (process.env.NODE_ENV === 'production') {
    return '';  // 使用相對路徑，讓前端和後端使用同一個域名
  }
  
  // 開發環境默認使用localhost
  return 'http://localhost:8000';
};

const API_BASE = getApiBase();

export interface DocumentUploadData {
  file: File;
  portionAmount?: number;
  portionUnit?: number;
  budget?: number;
  metadata?: {
    budget?: number;
    portionAmount?: number;
    portionUnit?: number;
  };
}

export interface SearchFilters {
  document_type?: string;
  dynasty?: string;
  artist?: string;
}

export interface SearchRequest {
  query: string;
  filters?: SearchFilters;
  top_k?: number;
}

// RAG Chat interfaces
export interface RAGChatRequest {
  message: string;
  domain?: string;
  session_id?: string;
  options?: {
    include_processing_steps?: boolean;
    [key: string]: any;
  };
}

export interface RAGChatResponse {
  success: boolean;
  data?: {
    session_id: string;
    response: string;  // 修改为response以匹配后端返回的字段
    sources?: Array<{
      title: string;
      content: string;
      score?: number;
      metadata?: any;
    }>;
    processing_steps?: Array<{
      step: string;
      description: string;
      duration?: number;
    }>;
    metadata?: {
      total_steps: number;
      processing_time: number;
      documents_found: number;
    };
  };
  error?: string;
}

export interface RAGSessionInfo {
  session_id: string;
  domain: string;
  created_at: string;
  last_activity: string;
  message_count: number;
  user_id?: string;
}

export interface RAGConversationHistory {
  session_id: string;
  conversation_history: Array<{
    timestamp: string;
    user_message: string;
    ai_response: string;
    sources?: any[];
    processing_steps?: any[];
  }>;
  message_count: number;
}

export interface Article {
  id: string;
  title: string;
  article_url: string | null;
  published_at: string;
  status: string;
  summary?: string;
}

export interface ArticleListResponse {
  documents: Article[];
}

export const ragApi = {

  async getArticles(): Promise<ArticleListResponse> {
    const response = await axios.get(`${API_BASE}/api/rag/documents`);
    return response.data;
  },
  
  // 搜索并重排序文档
  async searchAndRerankDocuments(request: SearchRequest) {
    const response = await axios.post(`${API_BASE}/api/rag/search_and_rerank`, request);
    return response.data;
  },

  // --- RAG Chat API Methods ---
  
  // Send message to RAG chat system
  async sendRAGMessage(request: RAGChatRequest): Promise<RAGChatResponse> {
    try {
      const response = await axios.post(`${API_BASE}/api/chat/rag_conversation`, request, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 600000, // 600 seconds (10 minutes) timeout
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        // Server responded with error status
        return {
          success: false,
          error: error.response.data?.detail || error.response.data?.message || 'Server error occurred'
        };
      } else if (error.request) {
        // Request timeout or network error
        return {
          success: false,
          error: 'Network error: Unable to connect to server'
        };
      } else {
        // Other error
        return {
          success: false,
          error: error.message || 'An unexpected error occurred'
        };
      }
    }
  },

  // Get RAG session information
  async getRAGSessionInfo(sessionId: string): Promise<{ success: boolean; data?: RAGSessionInfo; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE}/api/chat/rag_session/${sessionId}/info`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get session info'
      };
    }
  },

  // Get RAG conversation history
  async getRAGConversationHistory(sessionId: string): Promise<{ success: boolean; data?: RAGConversationHistory; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE}/api/chat/rag_session/${sessionId}/history`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get conversation history'
      };
    }
  },

  // Clear RAG session
  async clearRAGSession(sessionId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await axios.delete(`${API_BASE}/api/chat/rag_session/${sessionId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to clear session'
      };
    }
  },

  // List all active RAG sessions
  async listRAGSessions(): Promise<{ success: boolean; data?: { active_sessions: RAGSessionInfo[]; total_count: number }; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE}/api/chat/rag_sessions`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to list sessions'
      };
    }
  },
  // 上傳文檔
  async uploadDocument(data: DocumentUploadData) {
    const formData = new FormData();
    formData.append('file', data.file);
    
    // Prepare metadata with budget information
    const metadata: any = {};
    if (data.budget) metadata.budget = data.budget;
    if (data.portionAmount) metadata.portionAmount = data.portionAmount;
    if (data.portionUnit) metadata.portionUnit = data.portionUnit;
    
    if (Object.keys(metadata).length > 0) {
      formData.append('metadata', JSON.stringify(metadata));
    }
    
    const response = await axios.post(`${API_BASE}/api/rag/ingest`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  // 搜索文檔
  async searchDocuments(request: SearchRequest) {
    const response = await axios.post(`${API_BASE}/api/rag/search`, request);
    return response.data;
  },

  // 獲取文檔列表
  async getDocuments(page: number = 1, pageSize: number = 10, filters?: SearchFilters) {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (filters?.document_type) params.append('document_type', filters.document_type);
    if (filters?.dynasty) params.append('dynasty', filters.dynasty);
    
    const response = await axios.get(`${API_BASE}/api/rag/documents?${params}`);
    return response.data;
  },

  // 刪除文檔
  async deleteDocument(docId: string) {
    const response = await axios.delete(`${API_BASE}/api/rag/documents/${docId}`);
    return response.data;
  },

  // 健康檢查
  async healthCheck() {
    const response = await axios.get(`${API_BASE}/health`);
    return response.data;
  },
};